import { useEffect, useRef, useState, type ReactNode } from 'react';

import { loadKakaoMaps } from '@/lib/address/kakaoMap';

/**
 * 판정 구간 지도 — 카카오맵 (DOC-68 §8-B).
 *
 * ## 왜 카카오인가 (2026-08-27 결정)
 * 잠깐 OSM 타일로 띄웠지만 그 타일 서버는 **기부로 도는 커뮤니티 자원**이라 상용 서비스의 상시
 * 사용을 전제하지 않는다(OSMF 이용정책: 상업 서비스는 접근이 언제든 철회될 수 있음). 우리 제품의
 * 지도가 남의 기부금 위에 서 있으면, 막히는 날 화면에는 **회색 빈 칸**만 남는다. 카카오는 무료이고
 * 국내 지명·건물이 정확하며, 우리는 이미 같은 콘솔에 앱이 있다.
 *
 * ## 키가 없어도 화면은 선다
 * 지도는 판정의 **근거를 보여 주는 곁들이**다. 키가 없거나 도메인 등록이 빠져 지도가 못 뜨더라도
 * 거리·시간·CAP 은 그대로 나온다 — 그때 이 자리는 비지 않고 **왜 못 뜨는지**를 적는다.
 *
 * ## 거주지 핀은 모양이 다르다
 * 그 좌표는 집이 아니라 <b>시·군·구 중심</b>이다(DOC-47 §3.4-가). 현장과 같은 핀을 쓰면 보는 사람이
 * 집이라고 읽으므로, 반경 5km 원을 곁들여 「이 근처」 임을 모양으로 말한다.
 *
 * ## 한 벌만 만든다 (2026-08-27 · 마이페이지에서 꺼냈다)
 * 본인이 보는 마이페이지와 인사팀이 보는 승인 자리가 **같은 지도**를 써야 한다. 두 벌이면 ±5km
 * 원이 한쪽에만 그려지거나, 점선을 실선으로 고친 쪽이 「도로 경로」로 읽히기 시작한다 — 그 오해는
 * 곧 「시스템이 틀렸다」 가 된다(판정 거리는 도로거리이고 이 선은 직선이다).
 *
 * ## 좌표는 그때 값이지 지금 값이 아니다
 * 승인 화면이 넘기는 네 값은 <b>신청 시점에 굳은</b> 좌표다. 지금 다시 판정해 그리면, 그 사이에
 * 이사한 사람의 신청을 <b>다른 지도</b>로 보고 승인하게 된다.
 */
export function RelocationRouteMap({
  residenceLat,
  residenceLng,
  siteLat,
  siteLng,
  siteName,
  residenceLabel,
  minHeight = '340px',
  missingHint,
}: {
  residenceLat: number | null;
  residenceLng: number | null;
  siteLat: number | null;
  siteLng: number | null;
  siteName: string | null;
  residenceLabel: string | null;
  /** 자리 높이. 마이페이지는 남는 높이를 다 먹고(340px 이상), 모달은 낮게 잡는다. */
  minHeight?: string;
  /**
   * 좌표가 없을 때 적을 말. <b>부르는 쪽마다 다르다</b> — 본인 화면은 「거주지를 등록하면」 이고
   * 승인 화면은 「신청 당시 좌표가 없습니다」 다. 여기서 한 문장으로 뭉치면 둘 중 하나는 거짓말이다.
   */
  missingHint?: ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const ready = residenceLat != null && residenceLng != null && siteLat != null && siteLng != null;

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    let cleanupResize: (() => void) | null = null;
    // 뒷정리는 이 시점의 칸을 비워야 한다 — ref 는 그때 다른 노드를 가리키고 있을 수 있다.
    const box = boxRef.current;

    loadKakaoMaps()
      .then((kakao) => {
        // 화면을 떠난 뒤 도착한 응답으로 지도를 그리지 않는다.
        if (cancelled || !box) return;
        if (!kakao) {
          setMapError('지도 키가 아직 등록되지 않았습니다. 환경설정 › 외부 API 설정에서 카카오맵 키를 넣어 주세요.');
          return;
        }

        const { maps } = kakao;
        const home = new maps.LatLng(residenceLat, residenceLng);
        const site = new maps.LatLng(siteLat, siteLng);

        const map = new maps.Map(box, { center: site, level: 9, scrollwheel: false });

        // 라벨을 얹은 말풍선을 직접 그린다 — 기본 마커에는 이름이 없어 어느 핀이 무엇인지 범례가 필요해진다.
        const pin = (label: string, color: string) =>
          new maps.CustomOverlay({
            position: label.startsWith('현장') ? site : home,
            yAnchor: 1.2,
            zIndex: 3,
            content:
              `<div style="white-space:nowrap;font-size:11px;font-weight:700;color:#fff;background:${color};`
              + `padding:3px 8px;border-radius:10px;box-shadow:0 1px 4px rgba(0,0,0,.35)">${label}</div>`,
          });

        pin(`현장 · ${siteName ?? ''}`, '#c2410c').setMap(map);
        pin(`거주지 · ${residenceLabel ?? ''}`, '#1d4ed8').setMap(map);
        // 구 중심이라 생기는 ±5km 오차를 눈에 보이게 그린다 — 「경계 구간 · 담당자 확인」 이 왜 뜨는지가 여기서 읽힌다.
        new maps.Circle({
          center: home, radius: 5000,
          strokeWeight: 1, strokeColor: '#1d4ed8', strokeOpacity: 0.8, strokeStyle: 'shortdash',
          fillColor: '#1d4ed8', fillOpacity: 0.07,
        }).setMap(map);
        // 점선이다 — 도로 경로가 아니라 직선임을 모양으로 말한다(판정 거리는 도로거리다).
        new maps.Polyline({
          path: [home, site], strokeWeight: 2, strokeColor: '#64748b', strokeOpacity: 0.9, strokeStyle: 'dash',
        }).setMap(map);

        const bounds = new maps.LatLngBounds();
        bounds.extend(home);
        bounds.extend(site);
        map.setBounds(bounds, 40, 40, 40, 40);

        // 칸의 크기가 바뀌면(창 크기·탭 전환) 카카오맵은 <b>스스로 알아채지 못한다</b> —
        // 지도가 옛 크기로 잘린 채 남는다. 크기를 다시 알려 주고 구간도 다시 맞춘다.
        const observer = new ResizeObserver(() => {
          map.relayout();
          map.setBounds(bounds, 40, 40, 40, 40);
        });
        observer.observe(box);
        cleanupResize = () => observer.disconnect();
      })
      .catch(() => {
        if (!cancelled) setMapError('지도를 불러오지 못했습니다. 카카오 콘솔에 이 주소가 등록돼 있는지 확인해 주세요.');
      });

    return () => {
      cancelled = true;
      cleanupResize?.();
      // 카카오맵에는 파괴 함수가 없다. 칸을 비워야 다시 그릴 때 지도가 겹쳐 쌓이지 않는다.
      // innerHTML 대신 replaceChildren — 문자열을 HTML 로 해석시키지 않는다.
      box?.replaceChildren();
    };
  }, [ready, residenceLat, residenceLng, siteLat, siteLng, siteName, residenceLabel]);

  /*
    좌표가 없으면 칸을 비우지 않고 <b>왜 못 그리는지</b>를 적는다. 빈 칸은 고장으로 읽히고,
    실제로는 거의 언제나 사람이 고칠 수 있는 일이다(거주지 미등록 · 현장 주소 없음).
  */
  if (!ready || mapError) {
    return (
      <div
        className="rounded-lg flex items-center justify-center"
        style={{ border: '1px dashed var(--border)', height: '100%', minHeight, padding: '16px' }}
      >
        <p style={{ ...mutedText, textAlign: 'center', wordBreak: 'keep-all' }}>
          아직 지도를 그릴 수 없습니다.<br />
          {mapError ?? missingHint ?? (residenceLat == null
            ? '거주지가 등록되지 않았습니다.'
            : '배치 현장에 주소가 없습니다 — 현장 관리에서 주소를 넣어 주세요.')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: '100%' }}>
      {/* 남는 높이를 전부 먹는다(flex:1). 각주 한 줄만 아래에 남긴다. */}
      <div
        ref={boxRef}
        style={{ width: '100%', flex: 1, minHeight, borderRadius: '8px', border: '1px solid var(--border)', zIndex: 0 }}
      />
      <p style={{ ...mutedText, marginTop: '4px' }}>
        점선은 <b>직선</b>이며 판정 거리는 도로거리입니다. 파란 원은 거주지 좌표가 시·군·구 중심이라 생기는 ±5km 오차입니다.
        {' '}
        {/*
          `/link/to/` 만 주면 도착지만 찍히고 출발지는 카카오맵이 「현재 위치」 를 묻는다 — 우리가
          판정에 쓴 출발점(거주지)이 사라져 <b>다른 구간의 거리</b>가 나온다. from/to 를 함께 준다.
          출발지 좌표는 시·군·구 중심이라 카카오맵이 재는 거리도 그 기준이다.
        */}
        <a
          href={
            `https://map.kakao.com/link/from/${encodeURIComponent(residenceLabel ?? '거주지')},${residenceLat},${residenceLng}`
            + `/to/${encodeURIComponent(siteName ?? '현장')},${siteLat},${siteLng}`
          }
          target="_blank"
          rel="noreferrer"
          style={{ color: 'var(--accent)' }}
        >
          카카오맵에서 길찾기 열기
        </a>
      </p>
    </div>
  );
}

const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
